import React, { useState, useEffect } from 'react'
import { useLocation, Redirect } from 'react-router-dom'
import { App } from '@capacitor/app'
import {
	useActiveTabState,
	useMyPlayer,
	useMyPlayerId,
	useMyTeam,
	useMyGame,
	useSetCurrentPathname,
	useShowIntroScreenForPath,
    useUpdateSectionFromUrl,
} from './data'
import { labelRound, shortcutToSection, ucFirst } from './util'
import { sharePreviewPathToAppPath } from './previewShareUrl'
import { HOME_HOSTNAME, isValidTab } from './definitions'

type RedirectType = {
	url?: string
	game?: GameId
}

const APP_URL_SCHEME = 'com.maxbarry.autoroster'

//
// The type of useLocation() v5 is freaking bizarre.
//

type LocationData = {
	hash: string
	key: string
	pathname: string
	search: string
	state: null
}

type UseLocationData = LocationData | {
	action: string
	location: LocationData
}

const isAutoRosterWebUrl = (url: URL) => (
	(url.protocol === 'http:' || url.protocol === 'https:')
	&& url.hostname === HOME_HOSTNAME
)

const appPathFromUrl = (url: URL): string | null => {
	if (isAutoRosterWebUrl(url)) {
		return sharePreviewPathToAppPath(url.pathname, url.search)
			|| `${url.pathname}${url.search}`
	}

	if (url.protocol === `${APP_URL_SCHEME}:`) {
		const path = [ url.hostname, url.pathname.replace(/^\//, '') ]
			.filter(Boolean)
			.join('/')
		return `${path || ''}${url.search}`
	}

	return null
}

const SectionUpdater = () => {

	const locationData: UseLocationData = useLocation()

    const updateSectionFromUrl = useUpdateSectionFromUrl()

    const [ activeTab, setActiveTab ] = useActiveTabState()
	const setCurrentPathname = useSetCurrentPathname()

	const [ redirect, setRedirect ] = useState<RedirectType>()

	// console.log('SectionUpdater - locData', locationData)

	const { pathname, search } = ('location' in locationData ? locationData.location: locationData)
	const shareAppPath = sharePreviewPathToAppPath(pathname, search)
	const [ pathRoot ] = pathname.substring(1).split('/')
	// This duplicates the route shapes declared in Main.tsx. Keep both locations
	// synchronized so new or changed routes are not incorrectly accepted or rejected.
	const isFixedPath = /^\/(settings|roster|single-roster|trials|not-found|quick-start)\/?$/.test(pathname)
	const isJoinClubPath = /^\/join-club\/[^/]+\/[^/]+\/?$/.test(pathname)
	const isTabPath = pathRoot !== 'settings'
		&& pathRoot !== 'join-club'
		&& isValidTab(pathRoot)
	const isKnownPath = pathname === '/'
		|| isTabPath
		|| isFixedPath
		|| isJoinClubPath
		|| Boolean(shareAppPath)

	const showIntroScreen = useShowIntroScreenForPath(pathname)

	useEffect(() => {
		setCurrentPathname(pathname)
	}, [ pathname, setCurrentPathname ])

    useEffect(() => {
        // console.log('Adding appUrlOpen listener.')

		let cancelled = false

		const redirectToAppUrl = (url?: string) => {
			if (!cancelled && url) {
				const incomingUrl = new URL(url)
				const appPath = appPathFromUrl(incomingUrl)
				if (appPath !== null) {
                    console.log('Redirecting due to app URL...')
                    setRedirect({
						url: `${appPath}${incomingUrl.hash}`.replace(/^\//, ''),
                    })
                }
            }
		}

		void App.getLaunchUrl().then(data => {
			if (data?.url) {
				console.log('* App launched with URL:', data)
				redirectToAppUrl(data.url)
			}
		}).catch(error => {
			console.error('Failed to read app launch URL', error)
		})

        const listener = App.addListener('appUrlOpen', data => {
            console.log('* App opened with URL:', data)
			redirectToAppUrl(data.url)
        })

		return () => {
			cancelled = true
			void listener.then(handle => handle.remove())
		}
    }, [ ])

    //
    // Construct HTML page title, so it looks correct in web bookmarks & navigation
    //
    const team = useMyTeam()
	const game = useMyGame()
    const playerId = useMyPlayerId() || ''
    const player = useMyPlayer(playerId)

	const parts: Array<string> = [ ]

	if (team?.name) {
		parts.push(team.name)
	} else {
		parts.push('AutoRoster for Netball')
	}

    if (activeTab === 'games' && pathname !== '/games') {
        if (game?.round || game?.opponent) {
            const bits = [ ]
            if (game.round) {
                bits.push(labelRound(game.round))
            }
            if (game.opponent) {
                bits.push(`vs ${game.opponent}`)
            }
            parts.push(bits.join(' '))
        }
    } else if (activeTab === 'players' && pathname !== '/players') {
		if (player) {
			parts.push(player.name)
		}
	} else if (pathname) {
        parts.push(ucFirst(pathname.substring(1)))
    }

    //
	// Set HTML page title
	//
	document.title = /^\/quick-start\/?$/.test(pathname)
		? 'AutoRoster Quick Start Guide for club coaches'
		: parts.join(' | ')

    // console.log('SectionUpdater.js', pathname, redirect)

    //
    // Remember which is our active tab, because we want to know when
    // the user re-taps the same tab, and trigger a scroll event.
    //
	useEffect(() => {
		if (pathname) {
			const [ currentTab ] = pathname.substring(1).split(/[?/]/)
			if (currentTab && currentTab !== activeTab) {
				if (isValidTab(currentTab)) {
					setActiveTab(currentTab)
				} else if (!isFixedPath && !isJoinClubPath) {
					console.error("Weird tab", currentTab)
				}

			}
		}
	}, [ pathname, activeTab, setActiveTab, isFixedPath, isJoinClubPath ])

	//
	// Handle shortcuts & redirects
	//
	useEffect(() => {

		//
		// Translate a shortcut like ''?Pa8hWqKf0Y' into
		// 'team=Pa8hQynpLJ15CVECopgx&season=WqK8daFW2axTELADBfws&game=f0YDKMTlTFTuunE06RZq'
		// and send it to updateSectionFromUrl().
		//
		const shortcutToRedirect = async (shortcut: string) => {
			const newSection = await shortcutToSection(shortcut)
			const { team, season, game } = newSection

			//
			// Set 'team' and 'season' appropriately
			//
			updateSectionFromUrl('/', `team=${team}&season=${season}`)

			//
			// Send to correct 'game' page
			//
			setRedirect({ game })
		}

		if (shareAppPath) {
			setRedirect({
				url: shareAppPath.replace(/^\//, ''),
			})
		} else if (!isKnownPath) {
			setRedirect({
				url: 'not-found',
			})
		} else if (pathname === '/') {
			if (search && search.charAt(0) === '?' && !search.match('=')) {
				//
				// Have we been sent a shortcut? If so, redirect
				//
				// e.g. https://autoroster.io/?xFpWaw3b
				//
				shortcutToRedirect(search.substring(1))
			} else if (showIntroScreen) {
				// Not redirecting because of showIntroScreen
			} else {
				// Root! Need a redirect to games
				setRedirect({ })
			}
		} else {

			// updateSectionFromUrl(pathname, search)
			setRedirect(undefined)

		}

	}, [ pathname, search, updateSectionFromUrl, showIntroScreen, shareAppPath, isKnownPath ])

	//
	// If sent 'team', 'season', 'game', 'player', then update section
	//
	useEffect(() => {
		updateSectionFromUrl(pathname, search)
	}, [ pathname, search, updateSectionFromUrl ])

	if (redirect) {
		let page

		if (redirect.url) {
			page = redirect.url
		} else {
			page = 'games'
			if (redirect.game) {
				page += `/${redirect.game}`
			}
		}

		return (
			<Redirect
				to={`/${page}`}
			/>
		)
	}

	return null
}

export default SectionUpdater
